import * as THREE from 'three';
import type {
  ActiveSelectionTool,
  SelectionDragSession,
  SelectionToolContext,
} from './activeSelectionTool';

type Axis = 'x' | 'y' | 'z';

type MoveDragState = {
  mode: 'axis' | 'plane';
  axisName?: Axis;
  axis?: THREE.Vector3;
  plane: THREE.Plane;
  startPoint: THREE.Vector3;
  initialPositions: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
  }>;
};

const AXIS_VECTORS: Record<Axis, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

export class MoveSelectionTool implements ActiveSelectionTool {
  readonly id = 'move' as const;

  private readonly center = new THREE.Vector3();
  private readonly size = new THREE.Vector3();
  private readonly gizmoRoot = new THREE.Group();
  private readonly surfaceHandle = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthTest: false,
      side: THREE.DoubleSide,
    }),
  );
  private readonly gizmoTargets: THREE.Object3D[] = [];
  private readonly surfaceTargets: THREE.Object3D[] = [this.surfaceHandle];
  private visible = false;

  constructor() {
    this.gizmoRoot.visible = false;
    this.surfaceHandle.visible = false;
    this.surfaceHandle.renderOrder = 999;
    this.surfaceHandle.userData.dragMode = 'plane';
    this.surfaceHandle.userData.dragPriority = 0;
    this.createAxisGizmo();
  }

  render(parent: THREE.Object3D): void {
    parent.add(this.surfaceHandle);
    parent.add(this.gizmoRoot);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.gizmoRoot.visible = visible;
    this.surfaceHandle.visible = visible;
  }

  getPickTargets(): THREE.Object3D[] {
    if (!this.visible) {
      return [];
    }

    return [...this.gizmoTargets, ...this.surfaceTargets];
  }

  updateTransform(center: THREE.Vector3, size: THREE.Vector3): void {
    this.center.copy(center);
    this.size.copy(size);

    this.gizmoRoot.position.copy(this.center);
    this.surfaceHandle.position.copy(this.center);
    this.surfaceHandle.scale.set(
      Math.max(this.size.x, 0.2),
      Math.max(this.size.y, 0.2),
      Math.max(this.size.z, 0.2),
    );
  }

  startDrag(context: SelectionToolContext): SelectionDragSession | null {
    const dragMode = context.intersection.object.userData.dragMode as
      | 'axis'
      | 'plane'
      | undefined;

    if (dragMode === 'plane') {
      const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      dragPlane.constant = -this.center.y;

      const startPoint = context.ray.intersectPlane(
        dragPlane,
        new THREE.Vector3(),
      );
      if (!startPoint) {
        return null;
      }

      const dragState: MoveDragState = {
        mode: 'plane',
        plane: dragPlane,
        startPoint: startPoint.clone(),
        initialPositions: context.selectedObjects.map(object => ({
          object,
          position: object.position.clone(),
        })),
      };

      return this.createDragSession(dragState);
    }

    const axisName = context.intersection.object.userData.axis as
      | Axis
      | undefined;
    if (!axisName) {
      return null;
    }

    const axis = AXIS_VECTORS[axisName].clone();
    const cameraDirection = new THREE.Vector3();
    context.camera.getWorldDirection(cameraDirection);
    const dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      cameraDirection.normalize(),
      this.center,
    );

    const startPoint = context.ray.intersectPlane(
      dragPlane,
      new THREE.Vector3(),
    );
    if (!startPoint) {
      return null;
    }

    const dragState: MoveDragState = {
      mode: 'axis',
      axisName,
      axis,
      plane: dragPlane,
      startPoint: startPoint.clone(),
      initialPositions: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
      })),
    };

    return this.createDragSession(dragState);
  }

  dispose(): void {
    for (const target of this.gizmoTargets) {
      if (target instanceof THREE.Mesh) {
        target.geometry.dispose();
        if (Array.isArray(target.material)) {
          target.material.forEach(material => material.dispose());
        } else {
          target.material.dispose();
        }
      }
    }

    this.surfaceHandle.geometry.dispose();
    if (Array.isArray(this.surfaceHandle.material)) {
      this.surfaceHandle.material.forEach(material => material.dispose());
    } else {
      this.surfaceHandle.material.dispose();
    }
  }

  private createDragSession(dragState: MoveDragState): SelectionDragSession {
    return {
      updateDrag: (ray, snapToHalfStep) => {
        const dragPoint = ray.intersectPlane(
          dragState.plane,
          new THREE.Vector3(),
        );
        if (!dragPoint) {
          return;
        }

        if (dragState.mode === 'axis') {
          const axis = dragState.axis;
          const axisName = dragState.axisName;
          if (!axis || !axisName) {
            return;
          }

          let delta = axis.dot(dragPoint.clone().sub(dragState.startPoint));

          if (snapToHalfStep) {
            const selectedCount = dragState.initialPositions.length;

            if (selectedCount > 1) {
              delta = Math.round(delta / 0.5) * 0.5;
            } else if (selectedCount === 1) {
              const basePosition = dragState.initialPositions[0]!.position;

              switch (axisName) {
                case 'x': {
                  const snapped =
                    Math.round((basePosition.x + delta) / 0.5) * 0.5;
                  delta = snapped - basePosition.x;
                  break;
                }
                case 'y': {
                  const snapped =
                    Math.round((basePosition.y + delta) / 0.5) * 0.5;
                  delta = snapped - basePosition.y;
                  break;
                }
                case 'z': {
                  const snapped =
                    Math.round((basePosition.z + delta) / 0.5) * 0.5;
                  delta = snapped - basePosition.z;
                  break;
                }
              }
            }
          }

          for (const { object, position } of dragState.initialPositions) {
            object.setPosition(
              position.x + axis.x * delta,
              position.y + axis.y * delta,
              position.z + axis.z * delta,
            );
          }

          return;
        }

        const dragDelta = dragPoint.clone().sub(dragState.startPoint);
        let deltaX = dragDelta.x;
        let deltaZ = dragDelta.z;

        if (snapToHalfStep) {
          const selectedCount = dragState.initialPositions.length;

          if (selectedCount > 1) {
            deltaX = Math.round(deltaX / 0.5) * 0.5;
            deltaZ = Math.round(deltaZ / 0.5) * 0.5;
          } else if (selectedCount === 1) {
            const basePosition = dragState.initialPositions[0]!.position;
            const snappedX = Math.round((basePosition.x + deltaX) / 0.5) * 0.5;
            const snappedZ = Math.round((basePosition.z + deltaZ) / 0.5) * 0.5;
            deltaX = snappedX - basePosition.x;
            deltaZ = snappedZ - basePosition.z;
          }
        }

        for (const { object, position } of dragState.initialPositions) {
          object.setPosition(
            position.x + deltaX,
            position.y,
            position.z + deltaZ,
          );
        }
      },
    };
  }

  private createAxisGizmo() {
    const axisConfigs: Array<{
      axis: Axis;
      color: number;
      rotation: THREE.Euler;
    }> = [
      {
        axis: 'x',
        color: 0xff4d4d,
        rotation: new THREE.Euler(0, 0, -Math.PI / 2),
      },
      { axis: 'y', color: 0x52e36b, rotation: new THREE.Euler(0, 0, 0) },
      {
        axis: 'z',
        color: 0x4da3ff,
        rotation: new THREE.Euler(Math.PI / 2, 0, 0),
      },
    ];

    for (const config of axisConfigs) {
      const group = new THREE.Group();
      group.rotation.copy(config.rotation);

      const shaft = new THREE.Mesh(
        new THREE.CylinderGeometry(0.03, 0.03, 1.4, 8),
        new THREE.MeshBasicMaterial({
          color: config.color,
          depthTest: false,
          transparent: true,
          opacity: 0.9,
        }),
      );
      shaft.position.y = 0.7;
      shaft.renderOrder = 1000;

      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.09, 0.26, 12),
        new THREE.MeshBasicMaterial({
          color: config.color,
          depthTest: false,
        }),
      );
      cone.position.y = 1.53;
      cone.renderOrder = 1001;

      const pickHandle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.16, 0.16, 1.8, 8),
        new THREE.MeshBasicMaterial({
          visible: false,
          depthTest: false,
        }),
      );
      pickHandle.position.y = 0.95;
      pickHandle.userData.axis = config.axis;
      pickHandle.userData.dragMode = 'axis';
      pickHandle.userData.dragPriority = 1;

      group.add(shaft);
      group.add(cone);
      group.add(pickHandle);
      this.gizmoRoot.add(group);
      this.gizmoTargets.push(pickHandle);
    }
  }
}
